import { Download, ListMusic, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react'
import { PageHeader, pageClassName } from '@/components/ui/Page'
import { ErrorNotice } from '@/components/ui/Feedback'
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router'

import { useDeletePlaylist, usePlaylists, type ImportPlaylistResult } from '@/api/hooks'
import type { Playlist } from '@/api/types'
import {
  Button,
  Dialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  IconButton,
  Skeleton,
  toast,
} from '@/components/ui'

import { describeError, trackCountLabel } from './helpers'
import { ImportPlaylistDialog } from './ImportPlaylistDialog'
import { PlaylistFormDialog } from './PlaylistFormDialog'
import { PlaylistMosaic } from './PlaylistMosaic'

const PAGE = pageClassName('browse')
const GRID = 'grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 lg:grid-cols-4'

interface PlaylistCardProps {
  playlist: Playlist
  onRename: (playlist: Playlist) => void
  onDelete: (playlist: Playlist) => void
}

function PlaylistCard({ playlist, onRename, onDelete }: PlaylistCardProps) {
  return (
    <li className="relative">
      <Link
        to={`/playlists/${playlist.id}`}
        className="group block rounded-card focus-visible:outline-offset-4"
      >
        <PlaylistMosaic
          coverUrls={playlist.cover_urls}
          className="transition-opacity group-hover:opacity-90"
        />
        {/* The right padding keeps the title clear of the overflow button that
            sits over this block. */}
        <span className="mt-2 block pr-10">
          <span className="block truncate text-sm font-medium text-text">{playlist.name}</span>
          <span className="block truncate text-sm text-text-muted">
            {trackCountLabel(playlist.track_count)}
          </span>
        </span>
      </Link>

      {/* A sibling of the link, never nested inside it. */}
      <div className="absolute bottom-1 right-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton aria-label={`More options for ${playlist.name}`} size="sm">
              <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
            </IconButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem icon={Pencil} onSelect={() => onRename(playlist)}>
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem icon={Trash2} destructive onSelect={() => onDelete(playlist)}>
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </li>
  )
}

function PlaylistCardSkeleton() {
  return (
    <li>
      <Skeleton className="aspect-square w-full rounded-card" />
      <Skeleton className="mt-3 h-3.5 w-3/4" />
      <Skeleton className="mt-2 h-3 w-1/2" />
    </li>
  )
}

/**
 * What to say after an import.
 *
 * The counts only earn a mention when they disagree with each other: "imported
 * 100 tracks" needs no footnote, but silently dropping six because Deezer will
 * not serve them in this country does.
 */
function summariseImport(result: ImportPlaylistResult): string {
  const missing = result.source_total - result.imported
  const opened = `“${result.playlist.name}” is ready.`
  if (missing <= 0) return `${opened} Songs are added to your library and download automatically.`
  const reason = result.unavailable_here > 0 ? ' not available in this region' : ' could not be read'
  return `${opened} ${missing} of ${result.source_total}${reason}.`
}

export function PlaylistsPage({ renderLayout }: {
  renderLayout?: (actions: ReactNode, content: ReactNode) => ReactNode
}) {
  const { data: playlists, isPending, isError, error, refetch, isFetching } = usePlaylists()
  const deletePlaylist = useDeletePlaylist()

  const [formOpen, setFormOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [editing, setEditing] = useState<Playlist | null>(null)
  const [deleting, setDeleting] = useState<Playlist | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  function openCreate() {
    setEditing(null)
    setFormOpen(true)
  }

  function openRename(playlist: Playlist) {
    setEditing(playlist)
    setFormOpen(true)
  }

  function openDelete(playlist: Playlist) {
    setDeleteError(null)
    setDeleting(playlist)
  }

  function confirmDelete() {
    if (!deleting) return
    const target = deleting
    setDeleteError(null)
    deletePlaylist.mutate(target.id, {
      onSuccess: () => {
        setDeleting(null)
        toast.success('Playlist deleted', `“${target.name}” is gone. Your songs are untouched.`)
      },
      onError: (mutationError) =>
        setDeleteError(
          describeError(mutationError, 'Could not delete that playlist. Please try again.'),
        ),
    })
  }

  const actions = (
    <div className="flex shrink-0 items-center gap-2">
      <Button variant="secondary" onClick={() => setImportOpen(true)}>
        <Download className="h-4 w-4" aria-hidden="true" />
        {/* The word is the first thing to go when the screen is narrow;
            two full-width labels here wrap the header on a phone. */}
        <span className="hidden sm:inline">Import</span>
        <span className="sr-only sm:hidden">Import a playlist</span>
      </Button>
      <Button onClick={openCreate}>
        <Plus className="h-4 w-4" aria-hidden="true" />
        <span className="hidden sm:inline">New playlist</span>
        <span className="sr-only sm:hidden">New playlist</span>
      </Button>
    </div>
  )
  const content = (
    <>
      {renderLayout && playlists ? (
        <p className="mb-4 text-sm text-text-muted">
          {playlists.length} {playlists.length === 1 ? 'playlist' : 'playlists'}
        </p>
      ) : null}
      {isPending ? (
        <ul className={GRID} role="status" aria-label="Loading playlists">
          {Array.from({ length: 8 }, (_, position) => (
            <PlaylistCardSkeleton key={position} />
          ))}
        </ul>
      ) : isError ? (
        <ErrorNotice title="Could not load your playlists" busy={isFetching} onRetry={() => void refetch()}>
          {describeError(error, 'Check your connection and try again.')}
        </ErrorNotice>
      ) : playlists === undefined || playlists.length === 0 ? (
        <EmptyState
          icon={ListMusic}
          title="No playlists yet"
          description="Group songs however you like. Songs you add are also kept in your library."
          action={{ label: 'New playlist', onClick: openCreate, icon: Plus }}
          secondaryAction={{
            label: 'Import from a link',
            onClick: () => setImportOpen(true),
            icon: Download,
          }}
        />
      ) : (
        <ul className={GRID}>
          {playlists.map((playlist) => (
            <PlaylistCard
              key={playlist.id}
              playlist={playlist}
              onRename={openRename}
              onDelete={openDelete}
            />
          ))}
        </ul>
      )}

      <ImportPlaylistDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={(result) =>
          toast.success(
            `Imported ${result.imported} ${result.imported === 1 ? 'song' : 'songs'}`,
            summariseImport(result),
          )
        }
      />

      <PlaylistFormDialog
        key={editing?.id ?? 'new'}
        open={formOpen}
        onOpenChange={setFormOpen}
        playlist={editing ?? undefined}
        onSaved={(saved) =>
          toast.success(
            editing ? 'Playlist updated' : 'Playlist created',
            editing ? undefined : `“${saved.name}” is ready for songs.`,
          )
        }
      />

      <Dialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open && !deletePlaylist.isPending) {
            setDeleting(null)
            setDeleteError(null)
          }
        }}
        title="Delete this playlist?"
        description={
          deleting
            ? `“${deleting.name}” will be removed. The songs stay in your library.`
            : undefined
        }
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              disabled={deletePlaylist.isPending}
              onClick={() => setDeleting(null)}
            >
              Cancel
            </Button>
            <Button variant="danger" loading={deletePlaylist.isPending} onClick={confirmDelete}>
              Delete
            </Button>
          </>
        }
      >
        {deleteError ? (
          <p role="alert" className="text-sm text-danger">
            {deleteError}
          </p>
        ) : null}
      </Dialog>
    </>
  )

  return renderLayout ? renderLayout(actions, content) : (
    <div className={PAGE}>
      <PageHeader title="Playlists" actions={actions} />
      {content}
    </div>
  )
}
